import { getBrand, BrandConfig } from '@/config/brands';

export interface SEOMetadata {
  title: string;
  description: string;
  keywords: string[];
  canonicalUrl: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  ogType: string;
  twitterTitle: string;
  twitterDescription: string;
  twitterImage: string;
  twitterCard: 'summary' | 'summary_large_image' | 'app' | 'player';
  jsonLdSchema: any;
  robotsMeta: string;
  priority: number;
  changeFreq: 'always' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'never';
}

export interface PageTypeTemplate {
  pageType: string;
  template: SEOMetadata;
  dynamicFields: string[];
}

// Page type definitions for all routes
export const PAGE_TYPES = {
  HOME: 'home',
  SEARCH: 'search',
  HOSPITAL_PROFILE: 'hospital_profile',
  PROCEDURE_PROFILE: 'procedure_profile',
  HOSPITAL_MAP: 'hospital_map',
  CPT_CODES: 'cpt_codes',
  CPT_CODE_DETAIL: 'cpt_code_detail',
  DRG_CODES: 'drg_codes',
  DRG_CODE_DETAIL: 'drg_code_detail',
  REVENUE_CODES: 'revenue_codes',
  REVENUE_CODE_DETAIL: 'revenue_code_detail',
  DRUG_CODES: 'drug_codes',
  DRUG_CODE_DETAIL: 'drug_code_detail',
  SUPPLY_CODES: 'supply_codes', // For future implementation
  SUPPLY_CODE_DETAIL: 'supply_code_detail', // For future implementation
  HOSPITAL_RANKINGS: 'hospital_rankings',
  WORST_OFFENDERS: 'worst_offenders',
  EXTREME_PRICE_GOUGING: 'extreme_price_gouging',
  COMMON_PROCEDURES: 'common_procedures',
  DONATE: 'donate',
  // Consumer brand page types
  CONSUMER_SEARCH: 'consumer_search',
  CONSUMER_REPORT_CARD: 'consumer_report_card',
  CONSUMER_STATE: 'consumer_state',
  CONSUMER_COST_CALCULATOR: 'consumer_cost_calculator',
  CONSUMER_COMPARISON: 'consumer_comparison'
} as const;

// Default SEO templates for each page type
export const SEO_TEMPLATES: Record<string, PageTypeTemplate> = {
  [PAGE_TYPES.HOME]: {
    pageType: PAGE_TYPES.HOME,
    template: {
      title: "Luigi's Hall of Shame - Healthcare Price Transparency & Hospital Chargemaster Data",
      description: "Exposing healthcare price gouging across America. Compare hospital prices internationally and see real chargemaster data corruption. Find transparent healthcare pricing.",
      keywords: [
        'healthcare price transparency',
        'hospital chargemaster',
        'medical price gouging',
        'healthcare costs',
        'hospital pricing',
        'medical bills',
        'healthcare transparency',
        'hospital charges'
      ],
      canonicalUrl: 'https://luigishallofshame.com/',
      ogTitle: "Luigi's Hall of Shame - Healthcare Price Transparency",
      ogDescription: "Exposing healthcare price gouging across America. Compare hospital prices internationally and see real chargemaster data corruption.",
      ogImage: 'https://luigishallofshame.com/og-image.jpg',
      ogType: 'website',
      twitterTitle: "Luigi's Hall of Shame - Healthcare Price Transparency",
      twitterDescription: "Exposing healthcare price gouging across America. Compare hospital prices internationally.",
      twitterImage: 'https://luigishallofshame.com/twitter-image.jpg',
      twitterCard: 'summary_large_image',
      jsonLdSchema: {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: "Luigi's Hall of Shame",
        description: "Healthcare Price Transparency Platform",
        url: 'https://luigishallofshame.com',
        potentialAction: {
          '@type': 'SearchAction',
          target: 'https://luigishallofshame.com/search?q={search_term_string}',
          'query-input': 'required name=search_term_string'
        }
      },
      robotsMeta: 'index, follow',
      priority: 1.0,
      changeFreq: 'daily'
    },
    dynamicFields: []
  },

  [PAGE_TYPES.HOSPITAL_PROFILE]: {
    pageType: PAGE_TYPES.HOSPITAL_PROFILE,
    template: {
      title: '{hospitalName} - Hospital Pricing & Chargemaster Data | Luigi\'s Hall of Shame',
      description: 'View {hospitalName} pricing transparency data, chargemaster information, and compare medical procedure costs. Located in {city}, {state}.',
      keywords: [
        '{hospitalName} pricing',
        '{hospitalName} chargemaster',
        'hospital costs {city}',
        'medical prices {state}',
        'healthcare transparency',
        '{hospitalName} bills',
        'hospital charges {city} {state}'
      ],
      canonicalUrl: 'https://luigishallofshame.com/hospital/{hospitalId}',
      ogTitle: '{hospitalName} - Hospital Pricing Data',
      ogDescription: 'View {hospitalName} pricing transparency data and compare medical procedure costs in {city}, {state}.',
      ogImage: 'https://luigishallofshame.com/og-hospital.jpg',
      ogType: 'article',
      twitterTitle: '{hospitalName} - Hospital Pricing Data',
      twitterDescription: 'View {hospitalName} pricing data and compare medical costs in {city}, {state}.',
      twitterImage: 'https://luigishallofshame.com/twitter-hospital.jpg',
      twitterCard: 'summary_large_image',
      jsonLdSchema: {
        '@context': 'https://schema.org',
        '@type': 'Hospital',
        name: '{hospitalName}',
        description: 'Hospital pricing and chargemaster data for {hospitalName}',
        address: {
          '@type': 'PostalAddress',
          addressLocality: '{city}',
          addressRegion: '{state}',
          addressCountry: 'US'
        },
        url: 'https://luigishallofshame.com/hospital/{hospitalId}',
        priceRange: '{priceRange}'
      },
      robotsMeta: 'index, follow',
      priority: 0.8,
      changeFreq: 'weekly'
    },
    dynamicFields: ['hospitalName', 'hospitalId', 'city', 'state', 'priceRange']
  },

  [PAGE_TYPES.CPT_CODE_DETAIL]: {
    pageType: PAGE_TYPES.CPT_CODE_DETAIL,
    template: {
      title: 'CPT Code {cptCode} - {procedureName} | Hospital Price Comparison',
      description: 'Compare hospital prices for CPT code {cptCode} ({procedureName}) across different hospitals. See pricing variations and find affordable options.',
      keywords: [
        'CPT code {cptCode}',
        '{procedureName} cost',
        '{procedureName} price',
        'medical procedure pricing',
        'hospital cost comparison',
        '{procedureName} hospital charges',
        'CPT {cptCode} pricing'
      ],
      canonicalUrl: 'https://luigishallofshame.com/cpt/{cptCode}',
      ogTitle: 'CPT {cptCode} - {procedureName} Price Comparison',
      ogDescription: 'Compare hospital prices for {procedureName} (CPT {cptCode}) across different hospitals and locations.',
      ogImage: 'https://luigishallofshame.com/og-cpt.jpg',
      ogType: 'article',
      twitterTitle: 'CPT {cptCode} - {procedureName} Pricing',
      twitterDescription: 'Compare hospital prices for {procedureName} (CPT {cptCode}) across different hospitals.',
      twitterImage: 'https://luigishallofshame.com/twitter-cpt.jpg',
      twitterCard: 'summary_large_image',
      jsonLdSchema: {
        '@context': 'https://schema.org',
        '@type': 'MedicalProcedure',
        name: '{procedureName}',
        code: {
          '@type': 'MedicalCode',
          code: '{cptCode}',
          codingSystem: 'CPT'
        },
        description: 'Pricing information and hospital comparison for {procedureName}',
        url: 'https://luigishallofshame.com/cpt/{cptCode}',
        priceRange: '{priceRange}'
      },
      robotsMeta: 'index, follow',
      priority: 0.7,
      changeFreq: 'weekly'
    },
    dynamicFields: ['cptCode', 'procedureName', 'priceRange']
  },

  [PAGE_TYPES.DRG_CODE_DETAIL]: {
    pageType: PAGE_TYPES.DRG_CODE_DETAIL,
    template: {
      title: 'DRG Code {drgCode} - {procedureName} | Hospital Price Comparison',
      description: 'Compare hospital prices for DRG code {drgCode} ({procedureName}) across different hospitals. See pricing variations and inpatient costs.',
      keywords: [
        'DRG code {drgCode}',
        '{procedureName} cost',
        '{procedureName} price',
        'inpatient procedure pricing',
        'hospital cost comparison',
        '{procedureName} hospital charges',
        'DRG {drgCode} pricing'
      ],
      canonicalUrl: 'https://luigishallofshame.com/drg/{drgCode}',
      ogTitle: 'DRG {drgCode} - {procedureName} Price Comparison',
      ogDescription: 'Compare hospital prices for {procedureName} (DRG {drgCode}) across different hospitals and locations.',
      ogImage: 'https://luigishallofshame.com/og-drg.jpg',
      ogType: 'article',
      twitterTitle: 'DRG {drgCode} - {procedureName} Pricing',
      twitterDescription: 'Compare hospital prices for {procedureName} (DRG {drgCode}) across different hospitals.',
      twitterImage: 'https://luigishallofshame.com/twitter-drg.jpg',
      twitterCard: 'summary_large_image',
      jsonLdSchema: {
        '@context': 'https://schema.org',
        '@type': 'MedicalProcedure',
        name: '{procedureName}',
        code: {
          '@type': 'MedicalCode',
          code: '{drgCode}',
          codingSystem: 'DRG'
        },
        description: 'Inpatient pricing information and hospital comparison for {procedureName}',
        url: 'https://luigishallofshame.com/drg/{drgCode}',
        priceRange: '{priceRange}'
      },
      robotsMeta: 'index, follow',
      priority: 0.7,
      changeFreq: 'weekly'
    },
    dynamicFields: ['drgCode', 'procedureName', 'priceRange']
  },

  [PAGE_TYPES.REVENUE_CODE_DETAIL]: {
    pageType: PAGE_TYPES.REVENUE_CODE_DETAIL,
    template: {
      title: 'Revenue Code {revenueCode} - {serviceName} | Hospital Billing Analysis',
      description: 'Analyze hospital billing for revenue code {revenueCode} ({serviceName}). Compare charges across hospitals and understand billing patterns.',
      keywords: [
        'revenue code {revenueCode}',
        '{serviceName} billing',
        '{serviceName} charges',
        'hospital billing analysis',
        'revenue code pricing',
        '{serviceName} hospital costs',
        'billing transparency'
      ],
      canonicalUrl: 'https://luigishallofshame.com/revenue/{revenueCode}',
      ogTitle: 'Revenue Code {revenueCode} - {serviceName} Billing Analysis',
      ogDescription: 'Analyze hospital billing for {serviceName} (Revenue Code {revenueCode}) across different hospitals.',
      ogImage: 'https://luigishallofshame.com/og-revenue.jpg',
      ogType: 'article',
      twitterTitle: 'Revenue Code {revenueCode} - {serviceName} Billing',
      twitterDescription: 'Analyze hospital billing for {serviceName} (Revenue Code {revenueCode}).',
      twitterImage: 'https://luigishallofshame.com/twitter-revenue.jpg',
      twitterCard: 'summary_large_image',
      jsonLdSchema: {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: '{serviceName}',
        serviceType: 'Medical Service',
        provider: {
          '@type': 'Organization',
          name: 'Healthcare Providers'
        },
        description: 'Hospital billing analysis for {serviceName}',
        url: 'https://luigishallofshame.com/revenue/{revenueCode}',
        priceRange: '{priceRange}'
      },
      robotsMeta: 'index, follow',
      priority: 0.6,
      changeFreq: 'weekly'
    },
    dynamicFields: ['revenueCode', 'serviceName', 'priceRange']
  },

  [PAGE_TYPES.DRUG_CODE_DETAIL]: {
    pageType: PAGE_TYPES.DRUG_CODE_DETAIL,
    template: {
      title: 'Drug Code {drugCode} - {drugName} | Hospital Medication Pricing',
      description: 'Compare hospital prices for {drugName} (Drug Code {drugCode}) across different hospitals. See medication cost variations.',
      keywords: [
        'drug code {drugCode}',
        '{drugName} cost',
        '{drugName} price',
        'hospital medication pricing',
        'drug cost comparison',
        '{drugName} hospital charges',
        'pharmaceutical pricing'
      ],
      canonicalUrl: 'https://luigishallofshame.com/drugs/{drugCode}',
      ogTitle: 'Drug {drugCode} - {drugName} Hospital Pricing',
      ogDescription: 'Compare hospital prices for {drugName} (Drug Code {drugCode}) across different hospitals.',
      ogImage: 'https://luigishallofshame.com/og-drug.jpg',
      ogType: 'article',
      twitterTitle: 'Drug {drugCode} - {drugName} Pricing',
      twitterDescription: 'Compare hospital prices for {drugName} (Drug Code {drugCode}).',
      twitterImage: 'https://luigishallofshame.com/twitter-drug.jpg',
      twitterCard: 'summary_large_image',
      jsonLdSchema: {
        '@context': 'https://schema.org',
        '@type': 'Drug',
        name: '{drugName}',
        code: '{drugCode}',
        description: 'Hospital pricing information for {drugName}',
        url: 'https://luigishallofshame.com/drugs/{drugCode}',
        priceRange: '{priceRange}'
      },
      robotsMeta: 'index, follow',
      priority: 0.6,
      changeFreq: 'weekly'
    },
    dynamicFields: ['drugCode', 'drugName', 'priceRange']
  },

  [PAGE_TYPES.HOSPITAL_RANKINGS]: {
    pageType: PAGE_TYPES.HOSPITAL_RANKINGS,
    template: {
      title: 'Hospital Rankings - Most & Least Expensive Hospitals | Healthcare Price Transparency',
      description: 'Discover which hospitals charge the most and least for medical procedures. Compare hospital rankings by pricing transparency and cost efficiency.',
      keywords: [
        'hospital rankings',
        'most expensive hospitals',
        'cheapest hospitals',
        'hospital price comparison',
        'healthcare cost rankings',
        'hospital efficiency rankings',
        'medical cost analysis'
      ],
      canonicalUrl: 'https://luigishallofshame.com/hospital-rankings',
      ogTitle: 'Hospital Rankings - Most & Least Expensive Hospitals',
      ogDescription: 'Discover which hospitals charge the most and least for medical procedures. Compare hospital rankings by pricing.',
      ogImage: 'https://luigishallofshame.com/og-rankings.jpg',
      ogType: 'article',
      twitterTitle: 'Hospital Rankings - Price Comparison',
      twitterDescription: 'Discover which hospitals charge the most and least for medical procedures.',
      twitterImage: 'https://luigishallofshame.com/twitter-rankings.jpg',
      twitterCard: 'summary_large_image',
      jsonLdSchema: {
        '@context': 'https://schema.org',
        '@type': 'Dataset',
        name: 'Hospital Price Rankings',
        description: 'Comprehensive ranking of hospitals by pricing transparency and cost efficiency',
        url: 'https://luigishallofshame.com/hospital-rankings',
        creator: {
          '@type': 'Organization',
          name: "Luigi's Hall of Shame"
        }
      },
      robotsMeta: 'index, follow',
      priority: 0.8,
      changeFreq: 'daily'
    },
    dynamicFields: []
  },

  [PAGE_TYPES.CONSUMER_SEARCH]: {
    pageType: PAGE_TYPES.CONSUMER_SEARCH,
    template: {
      title: 'Hospital Prices for {procedure} near {zip} | American Hospital Prices',
      description: 'Compare hospital prices for {procedure} near ZIP code {zip}. See real prices from {resultCount} hospitals. Know before you go.',
      keywords: [
        '{procedure} cost near me',
        'hospital prices {zip}',
        '{procedure} hospital price',
        'compare hospital costs',
        'medical procedure prices near {zip}'
      ],
      canonicalUrl: 'https://americanhospitalprices.com/search?q={procedure}&zip={zip}',
      ogTitle: '{procedure} Hospital Prices near {zip}',
      ogDescription: 'Compare {procedure} prices at hospitals near {zip}. Real prices, real savings.',
      ogImage: 'https://americanhospitalprices.com/consumer/og-search.png',
      ogType: 'website',
      twitterTitle: '{procedure} Hospital Prices near {zip}',
      twitterDescription: 'Compare {procedure} prices at hospitals near {zip}.',
      twitterImage: 'https://americanhospitalprices.com/consumer/og-search.png',
      twitterCard: 'summary_large_image',
      jsonLdSchema: {
        '@context': 'https://schema.org',
        '@type': 'SearchResultsPage',
        name: 'Hospital Price Search Results',
        description: 'Hospital prices for {procedure} near {zip}',
        url: 'https://americanhospitalprices.com/search?q={procedure}&zip={zip}'
      },
      robotsMeta: 'index, follow',
      priority: 0.7,
      changeFreq: 'daily'
    },
    dynamicFields: ['procedure', 'zip', 'resultCount']
  },

  [PAGE_TYPES.CONSUMER_REPORT_CARD]: {
    pageType: PAGE_TYPES.CONSUMER_REPORT_CARD,
    template: {
      title: '{hospitalName} Price Report Card | Grade: {grade} | American Hospital Prices',
      description: '{hospitalName} in {city}, {state} received a price fairness grade of {grade}. See how their prices compare to nearby hospitals.',
      keywords: [
        '{hospitalName} prices',
        '{hospitalName} price report card',
        '{hospitalName} grade',
        'hospital prices {city} {state}',
        'hospital price fairness'
      ],
      canonicalUrl: 'https://americanhospitalprices.com/report-card/{hospitalId}',
      ogTitle: '{hospitalName} — Grade: {grade}',
      ogDescription: 'See how {hospitalName} prices compare to nearby hospitals. Grade: {grade}.',
      ogImage: 'https://americanhospitalprices.com/consumer/og-report-card.png',
      ogType: 'article',
      twitterTitle: '{hospitalName} — Grade: {grade}',
      twitterDescription: 'See {hospitalName} price report card and comparison.',
      twitterImage: 'https://americanhospitalprices.com/consumer/og-report-card.png',
      twitterCard: 'summary_large_image',
      jsonLdSchema: {
        '@context': 'https://schema.org',
        '@type': 'Hospital',
        name: '{hospitalName}',
        address: {
          '@type': 'PostalAddress',
          addressLocality: '{city}',
          addressRegion: '{state}',
          addressCountry: 'US'
        },
        review: {
          '@type': 'Review',
          reviewRating: {
            '@type': 'Rating',
            ratingValue: '{grade}',
            bestRating: 'A',
            worstRating: 'F'
          },
          author: {
            '@type': 'Organization',
            name: 'American Hospital Prices'
          }
        }
      },
      robotsMeta: 'index, follow',
      priority: 0.8,
      changeFreq: 'weekly'
    },
    dynamicFields: ['hospitalName', 'hospitalId', 'city', 'state', 'grade']
  },

  [PAGE_TYPES.CONSUMER_STATE]: {
    pageType: PAGE_TYPES.CONSUMER_STATE,
    template: {
      title: '{stateName} Hospital Price Comparison | American Hospital Prices',
      description: 'Compare hospital prices across {stateName}. See which hospitals charge the most and least for common procedures in {stateName}.',
      keywords: [
        '{stateName} hospital prices',
        '{stateName} hospital comparison',
        'cheapest hospitals {stateName}',
        'hospital costs {stateName}',
        '{stateName} healthcare prices'
      ],
      canonicalUrl: 'https://americanhospitalprices.com/state/{stateCode}',
      ogTitle: '{stateName} Hospital Price Comparison',
      ogDescription: 'Compare hospital prices across {stateName}. Find the most and least expensive hospitals.',
      ogImage: 'https://americanhospitalprices.com/consumer/og-state.png',
      ogType: 'article',
      twitterTitle: '{stateName} Hospital Prices',
      twitterDescription: 'Compare hospital prices across {stateName}.',
      twitterImage: 'https://americanhospitalprices.com/consumer/og-state.png',
      twitterCard: 'summary_large_image',
      jsonLdSchema: {
        '@context': 'https://schema.org',
        '@type': 'Dataset',
        name: '{stateName} Hospital Price Comparison',
        description: 'Hospital price comparison data for {stateName}',
        url: 'https://americanhospitalprices.com/state/{stateCode}',
        spatialCoverage: '{stateName}'
      },
      robotsMeta: 'index, follow',
      priority: 0.8,
      changeFreq: 'weekly'
    },
    dynamicFields: ['stateName', 'stateCode']
  },

  [PAGE_TYPES.CONSUMER_COST_CALCULATOR]: {
    pageType: PAGE_TYPES.CONSUMER_COST_CALCULATOR,
    template: {
      title: 'Surgery Cost Calculator | Estimate Your Hospital Bill | American Hospital Prices',
      description: 'Estimate your hospital bill before your procedure. Free surgery cost calculator using real hospital price data from across America.',
      keywords: [
        'surgery cost calculator',
        'hospital bill estimator',
        'medical procedure cost',
        'hospital cost estimate',
        'how much does surgery cost'
      ],
      canonicalUrl: 'https://americanhospitalprices.com/calculator',
      ogTitle: 'Surgery Cost Calculator — Estimate Your Bill',
      ogDescription: 'Free surgery cost calculator using real hospital price data. Know before you go.',
      ogImage: 'https://americanhospitalprices.com/consumer/og-calculator.png',
      ogType: 'website',
      twitterTitle: 'Surgery Cost Calculator',
      twitterDescription: 'Estimate your hospital bill using real price data.',
      twitterImage: 'https://americanhospitalprices.com/consumer/og-calculator.png',
      twitterCard: 'summary_large_image',
      jsonLdSchema: {
        '@context': 'https://schema.org',
        '@type': 'WebApplication',
        name: 'Surgery Cost Calculator',
        description: 'Estimate hospital costs using real price data',
        url: 'https://americanhospitalprices.com/calculator',
        applicationCategory: 'HealthApplication'
      },
      robotsMeta: 'index, follow',
      priority: 0.7,
      changeFreq: 'monthly'
    },
    dynamicFields: []
  },

  [PAGE_TYPES.CONSUMER_COMPARISON]: {
    pageType: PAGE_TYPES.CONSUMER_COMPARISON,
    template: {
      title: 'Compare {hospitalA} vs {hospitalB} Prices | American Hospital Prices',
      description: 'Side-by-side price comparison of {hospitalA} and {hospitalB}. See which hospital charges more for common procedures.',
      keywords: [
        '{hospitalA} vs {hospitalB}',
        'hospital price comparison',
        'compare hospital costs',
        'hospital side by side'
      ],
      canonicalUrl: 'https://americanhospitalprices.com/compare/{hospitalAId}/{hospitalBId}',
      ogTitle: '{hospitalA} vs {hospitalB} — Price Comparison',
      ogDescription: 'Side-by-side price comparison of {hospitalA} and {hospitalB}.',
      ogImage: 'https://americanhospitalprices.com/consumer/og-compare.png',
      ogType: 'article',
      twitterTitle: '{hospitalA} vs {hospitalB}',
      twitterDescription: 'Side-by-side hospital price comparison.',
      twitterImage: 'https://americanhospitalprices.com/consumer/og-compare.png',
      twitterCard: 'summary_large_image',
      jsonLdSchema: {
        '@context': 'https://schema.org',
        '@type': 'ComparisonPage',
        name: '{hospitalA} vs {hospitalB} Price Comparison',
        url: 'https://americanhospitalprices.com/compare/{hospitalAId}/{hospitalBId}'
      },
      robotsMeta: 'index, follow',
      priority: 0.6,
      changeFreq: 'weekly'
    },
    dynamicFields: ['hospitalA', 'hospitalB', 'hospitalAId', 'hospitalBId']
  }
};

// Generate brand-specific SEO templates
const getBrandSEOTemplates = (hostname?: string): Record<string, PageTypeTemplate> => {
  const brand = getBrand(hostname);
  const baseTemplates = { ...SEO_TEMPLATES };

  // Update home template with brand-specific data
  baseTemplates[PAGE_TYPES.HOME] = {
    ...baseTemplates[PAGE_TYPES.HOME],
    template: {
      ...baseTemplates[PAGE_TYPES.HOME].template,
      title: brand.seo.title,
      description: brand.seo.description,
      keywords: brand.seo.keywords,
      canonicalUrl: brand.primaryDomain + '/',
      ogTitle: brand.seo.title,
      ogDescription: brand.seo.description,
      ogImage: brand.social.ogImage,
      twitterTitle: brand.seo.title,
      twitterDescription: brand.seo.description,
      twitterImage: brand.social.ogImage,
      jsonLdSchema: {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: brand.name,
        description: brand.description,
        url: brand.primaryDomain,
        potentialAction: {
          '@type': 'SearchAction',
          target: brand.primaryDomain + '/search?q={search_term_string}',
          'query-input': 'required name=search_term_string'
        }
      }
    }
  };

  // Update other templates with brand-specific URLs and branding
  Object.keys(baseTemplates).forEach(pageType => {
    const template = baseTemplates[pageType];
    if (template.template.canonicalUrl.includes('luigishallofshame.com')) {
      template.template.canonicalUrl = template.template.canonicalUrl.replace(
        'https://luigishallofshame.com',
        brand.primaryDomain
      );
    }
    if (template.template.ogImage.includes('luigishallofshame.com')) {
      template.template.ogImage = template.template.ogImage.replace(
        'https://luigishallofshame.com',
        brand.primaryDomain
      );
    }
    if (template.template.twitterImage.includes('luigishallofshame.com')) {
      template.template.twitterImage = template.template.twitterImage.replace(
        'https://luigishallofshame.com',
        brand.primaryDomain
      );
    }
    if (template.template.jsonLdSchema?.url?.includes?.('luigishallofshame.com')) {
      template.template.jsonLdSchema.url = template.template.jsonLdSchema.url.replace(
        'https://luigishallofshame.com',
        brand.primaryDomain
      );
    }
  });

  return baseTemplates;
};

// Utility functions for SEO metadata generation
export const generateSEOMetadata = (pageType: string, dynamicData: Record<string, string> = {}, hostname?: string): SEOMetadata => {
  const brandTemplates = getBrandSEOTemplates(hostname);
  const template = brandTemplates[pageType];
  if (!template) {
    throw new Error(`No SEO template found for page type: ${pageType}`);
  }

  const metadata = { ...template.template };

  // Replace dynamic placeholders in all string fields
  const replaceTokens = (text: string): string => {
    let result = text;
    template.dynamicFields.forEach(field => {
      if (dynamicData[field]) {
        const regex = new RegExp(`{${field}}`, 'g');
        result = result.replace(regex, dynamicData[field]);
      }
    });
    return result;
  };

  metadata.title = replaceTokens(metadata.title);
  metadata.description = replaceTokens(metadata.description);
  metadata.canonicalUrl = replaceTokens(metadata.canonicalUrl);
  metadata.ogTitle = replaceTokens(metadata.ogTitle);
  metadata.ogDescription = replaceTokens(metadata.ogDescription);
  metadata.twitterTitle = replaceTokens(metadata.twitterTitle);
  metadata.twitterDescription = replaceTokens(metadata.twitterDescription);

  // Replace tokens in keywords array
  metadata.keywords = metadata.keywords.map(keyword => replaceTokens(keyword));

  // Replace tokens in JSON-LD schema
  metadata.jsonLdSchema = JSON.parse(JSON.stringify(metadata.jsonLdSchema).replace(/{(\w+)}/g, (match, field) => {
    return dynamicData[field] || match;
  }));

  return metadata;
};

// Static page generation priorities
export const STATIC_GENERATION_PRIORITIES = {
  [PAGE_TYPES.HOME]: 1,
  [PAGE_TYPES.HOSPITAL_RANKINGS]: 2,
  [PAGE_TYPES.HOSPITAL_PROFILE]: 3,
  [PAGE_TYPES.CPT_CODE_DETAIL]: 4,
  [PAGE_TYPES.DRG_CODE_DETAIL]: 5,
  [PAGE_TYPES.REVENUE_CODE_DETAIL]: 6,
  [PAGE_TYPES.DRUG_CODE_DETAIL]: 7,
  [PAGE_TYPES.COMMON_PROCEDURES]: 8,
  [PAGE_TYPES.WORST_OFFENDERS]: 9,
  [PAGE_TYPES.EXTREME_PRICE_GOUGING]: 10
};